import api from '../../../api/apiClient';
import { config } from '../../../config';
import { formatDate, formatToday } from '../../shared/utils/misc';

const unwrap = async (promise) => {
  const res = await promise;
  return res.data;
};

export async function submitFeedback(id, data) {
  if (!id || !data) throw new Error('Employee id and feedback content are required');

  const payload = {
    employee_id: id,
    subject: data.subject,
    message: data.message,
  };

  await unwrap(await api.post('/api/feedback', payload));
}

/**
 * `feedbacks.action` is free text: null/Active while the report is open, then the outcome HR
 * chose. Each board button records its own outcome so the closed list can tell them apart.
 */
export const FEEDBACK_OUTCOMES = {
  RESOLVED: 'Resolved',
  VERBAL_ACTION: 'Verbal Action',
  WARNING_LETTER: 'Warning Letter',
};

const API_FEEDBACK_STATUS_MAP = {
  Active: 'pending',
  [FEEDBACK_OUTCOMES.RESOLVED]: 'closed',
  [FEEDBACK_OUTCOMES.VERBAL_ACTION]: 'closed',
  [FEEDBACK_OUTCOMES.WARNING_LETTER]: 'closed',
};

/** What the button chose → what is stored in `action`. */
const RESOLUTION_TO_OUTCOME = {
  resolve: FEEDBACK_OUTCOMES.RESOLVED,
  'verbal-action': FEEDBACK_OUTCOMES.VERBAL_ACTION,
  'warning-letter': FEEDBACK_OUTCOMES.WARNING_LETTER,
};

/**
 * `warning_letter` holds the uploaded file's id (older rows may hold a URL). An id is opened
 * through the API's file endpoint; a bare id used as an href resolved against the admin site.
 */
function warningLetterUrl(value) {
  if (value == null || value === '') return null;
  const str = String(value);
  if (/^https?:\/\//i.test(str)) return str;
  if (/^\d+$/.test(str)) return `${config.env.API_URL}/api/file/${str}`;
  return str;
}

export async function getAdminFeedback() {
  const { data: raw } = await unwrap(await api.get('/api/feedback/hr'));

  const normalized = { pending: [], closed: [] };

  raw.forEach((item) => {
    const uiStatus = API_FEEDBACK_STATUS_MAP[item.status] || 'pending';

    normalized[uiStatus].push({
      id: item.id,
      date: formatDate(item.requested_date),
      employeeId: item.employee_id,
      employeeName: item.employee_name,
      email: item.email ?? '',
      department: item.department,
      subject: item.subject,
      message: item.message,
      status: uiStatus,
      // Rows closed before outcomes were recorded separately all say Resolved; the letter tells.
      outcome:
        item.warning_letter && item.status === FEEDBACK_OUTCOMES.RESOLVED
          ? FEEDBACK_OUTCOMES.WARNING_LETTER
          : item.status,
      warningLetter: warningLetterUrl(item.warning_letter),
      actionDate: item.action_date ? formatDate(item.action_date) : null,
    });
  });

  return normalized;
}

export async function takeFeedbackAction(id, data) {
  if (!id) throw new Error('Feedback ID is required');

  const action = RESOLUTION_TO_OUTCOME[data?.resolutionType];
  if (!action) throw new Error('Unknown feedback action');

  const payload = {
    action,
    action_date: formatToday('YYYY-MM-DD'),
    warning_letter: data?.warningLetterId ?? null,
  };

  await unwrap(await api.put(`/api/feedback/hr/${id}`, payload));
}
